import React, { useState, useEffect, useRef, useMemo } from 'react';

import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import SaveIcon from '@material-ui/icons/Save';
import Paper from '@material-ui/core/Paper';
import MaterialIcon from '../../components/MaterialIcon';
import MenuItem from '@material-ui/core/MenuItem';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';

import L from 'leaflet';
import Config from '../../constants/appConfig';

const Box = (props) => {
  const {
    res,
    onClose,
    update,
    item,
    type,
    registeredAreas,
    registeredAreaClients,
    registeredAreaProperties,
    registeredAreaFolders,
    registeredAreasByClient,
    onRegisteredAreaClientChange,
    runways
  } = props;

  const guid = item ? item.guid : '';
  const [name, setName] = useState(item ? item.name : '');
  const [lng, setLng] = useState(item ? item.lng : '');
  const [lat, setLat] = useState(item ? item.lat : '');
  const [img_place, setImage] = useState(item ? item.img_place : '');
  const points_place = item ? item.points_place : '';
  const [polygonType, setPolygonType] = useState(item ? (item.poly_type || item.type || 'N') : 'N');

  const [areaTab, setAreaTab] = useState(0);

  const [selectedAreaGuid, setSelectedAreaGuid] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [selectedTreeAreaGuid, setSelectedTreeAreaGuid] = useState('');
  const [selectedPointsPlace, setSelectedPointsPlace] = useState('');
  const [selectedRunwayValue, setSelectedRunwayValue] = useState(item ? String(item.guid || item.id || '') : '');

  const mapRef = useRef(null);
  const layerRef = useRef(null);

  const displayPointsPlace = selectedPointsPlace || points_place;
  const titleType = type === 'Polygon' ? res.POLY : type === 'Point' ? res.PISTA : type;

  useEffect(() => {
    if (!displayPointsPlace) {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      layerRef.current = null;
      return;
    }

    let points = [];
    try {
      points = JSON.parse(displayPointsPlace);
    } catch (e) {
      points = [];
    }

    if (!Array.isArray(points) || !points.length) {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      layerRef.current = null;
      return;
    }

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    mapRef.current = L.map('mapid', {
      zoomControl: false,
      attributionControl: false
    }).setView(new L.LatLng(40.737, -73.923), 14);

    L.tileLayer(Config.tile_google, Config.tile_google_config).addTo(mapRef.current);

    if (points.length > 1) {
      const myStyle = {
        color: '#47839e',
        weight: 4,
        opacity: 0.5
      };

      layerRef.current = L.polygon(points, myStyle);
      layerRef.current.addTo(mapRef.current);
      mapRef.current.fitBounds(layerRef.current.getBounds());
    } else {
      layerRef.current = L.marker(points[0]);
      layerRef.current.addTo(mapRef.current);
      mapRef.current.setView(points[0], 14);
    }

    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 200);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      layerRef.current = null;
    };
  }, [displayPointsPlace]);

  const handleChange = (call) => (event) => {
    call(event.target.value);
  };

  const loadImage = file => {
    if (file.target.files.length > 0) {
      let filePath = file.target.files[0];

      const fileReader = new FileReader();
      fileReader.onloadend = () => {
        setImage(fileReader.result);
      };
      fileReader.readAsDataURL(filePath);
    }
  };

  const applySelectedArea = (sel) => {
    if (!sel) return;

    setPolygonType(sel.poly_type || sel.type || 'N');
    setName(sel.name || '');

    let rawPoints = '';
    if (sel.points) {
      rawPoints = String(sel.points);
    } else if (sel.points_place) {
      rawPoints = String(sel.points_place);
    }

    setSelectedPointsPlace(rawPoints);

    let latValue = '';
    let lngValue = '';
    if (rawPoints) {
      try {
        const pts = JSON.parse(rawPoints);
        if (Array.isArray(pts) && pts.length > 0 && Array.isArray(pts[0]) && pts[0].length >= 2) {
          const latNum = Number(pts[0][0]);
          const lngNum = Number(pts[0][1]);

          if (!isNaN(latNum) && !isNaN(lngNum)) {
            latValue = latNum.toFixed(6);
            lngValue = lngNum.toFixed(6);
          }
        }
      } catch (e) { }
    }

    setLat(latValue !== '' ? String(latValue) : (sel.lat || ''));
    setLng(lngValue !== '' ? String(lngValue) : (sel.lng || ''));
    setImage(sel.img_place || '');
  };

  const applySelectedRunway = (sel) => {
    if (!sel) return;

    setSelectedRunwayValue(String(sel.guid || sel.id || ''));
    setName(sel.name || '');
    setLat(sel.lat != null ? String(sel.lat) : '');
    setLng(sel.lng != null ? String(sel.lng) : '');
    setImage(sel.img_place || '');
    setSelectedPointsPlace(JSON.stringify([[Number(sel.lat), Number(sel.lng)]]));
  };

  const clearAppliedAreaData = () => {
    setPolygonType('N');
    setSelectedPointsPlace('');
    setName('');
    setLat('');
    setLng('');
    setImage('');
  };

  const handleSelectArea = (event) => {
    const selGuid = event.target.value;
    setSelectedAreaGuid(selGuid);

    if (!selGuid) {
      clearAppliedAreaData();
      return;
    }

    if (!registeredAreas || !registeredAreas.length) return;

    const sel = registeredAreas.find(a => String(a.guid) === String(selGuid));
    if (!sel) return;

    applySelectedArea(sel);
  };

  const handleSelectRunway = (event) => {
    const selValue = event.target.value;
    setSelectedRunwayValue(selValue);

    if (!selValue) {
      clearAppliedAreaData();
      return;
    }

    if (!runways || !runways.length) return;

    const sel = runways.find(a => String(a.guid || a.id) === String(selValue));
    if (!sel) return;

    applySelectedRunway(sel);
  };

  const handleSelectClient = (event) => {
    const clientId = event.target.value;

    setSelectedClientId(clientId);
    setSelectedPropertyId('');
    setSelectedFolderId('');
    setSelectedTreeAreaGuid('');
    clearAppliedAreaData();

    if (typeof onRegisteredAreaClientChange === 'function') {
      onRegisteredAreaClientChange(clientId);
    }
  };

  const handleSelectProperty = (event) => {
    const propertyId = event.target.value;
    setSelectedPropertyId(propertyId);
    setSelectedFolderId('');
    setSelectedTreeAreaGuid('');
    clearAppliedAreaData();
  };

  const handleSelectFolder = (event) => {
    const folderId = event.target.value;
    setSelectedFolderId(folderId);
    setSelectedTreeAreaGuid('');
    clearAppliedAreaData();
  };

  const filteredProperties = useMemo(() => {
    const properties = Array.isArray(registeredAreaProperties) ? registeredAreaProperties : [];
    return properties;
  }, [registeredAreaProperties]);

  const filteredFolders = useMemo(() => {
    const folders = Array.isArray(registeredAreaFolders) ? registeredAreaFolders : [];

    if (!selectedPropertyId) return [];

    return folders.filter(folder => String(folder.id_property) === String(selectedPropertyId));
  }, [registeredAreaFolders, selectedPropertyId]);

  const filteredTreeAreas = useMemo(() => {
    let areas = Array.isArray(registeredAreasByClient) ? [...registeredAreasByClient] : [];

    if (!selectedPropertyId) return [];

    areas = areas.filter(area => String(area.id_property) === String(selectedPropertyId));

    if (selectedFolderId) {
      areas = areas.filter(area => String(area.id_folder_area) === String(selectedFolderId));
    }

    return areas;
  }, [registeredAreasByClient, selectedPropertyId, selectedFolderId]);

  const handleSelectTreeArea = (event) => {
    const selGuid = event.target.value;
    setSelectedTreeAreaGuid(selGuid);

    if (!selGuid) {
      clearAppliedAreaData();
      return;
    }

    if (!filteredTreeAreas || !filteredTreeAreas.length) return;

    const sel = filteredTreeAreas.find(a => String(a.guid) === String(selGuid));
    if (!sel) return;

    applySelectedArea(sel);
  };

  const clearSemVinculoArea = () => {
    setSelectedAreaGuid('');
    clearAppliedAreaData();
  };

  const clearRunwayStep = () => {
    setSelectedRunwayValue('');
    clearAppliedAreaData();
  };

  const clearClientStep = () => {
    setSelectedClientId('');
    setSelectedPropertyId('');
    setSelectedFolderId('');
    setSelectedTreeAreaGuid('');
    clearAppliedAreaData();

    if (typeof onRegisteredAreaClientChange === 'function') {
      onRegisteredAreaClientChange('');
    }
  };

  const clearPropertyStep = () => {
    setSelectedPropertyId('');
    setSelectedFolderId('');
    setSelectedTreeAreaGuid('');
    clearAppliedAreaData();
  };

  const clearFolderStep = () => {
    setSelectedFolderId('');
    setSelectedTreeAreaGuid('');
    clearAppliedAreaData();
  };

  const clearTreeAreaStep = () => {
    setSelectedTreeAreaGuid('');
    clearAppliedAreaData();
  };

  const handleChangeTab = (event, newValue) => {
    setAreaTab(newValue);
    clearAppliedAreaData();

    if (newValue === 0) {
      setSelectedClientId('');
      setSelectedPropertyId('');
      setSelectedFolderId('');
      setSelectedTreeAreaGuid('');
      if (typeof onRegisteredAreaClientChange === 'function') {
        onRegisteredAreaClientChange('');
      }
    } else {
      setSelectedAreaGuid('');
    }
  };

  const updateData = () => {
    let itemData = {
      guid: guid,
      name: name,
      lat: lat,
      lng: lng,
      img_place: img_place
    };
    if (type === 'Polygon') itemData.poly_type = polygonType;

    if (selectedPointsPlace) {
      itemData.points_place = selectedPointsPlace;
    } else if (points_place) {
      itemData.points_place = points_place;
    }

    if (selectedAreaGuid || selectedTreeAreaGuid) {
      itemData.type = 'area';
    }

    if (selectedRunwayValue && type === 'Point') {
      itemData.type = 'point';
    }

    let actionType = 'new';
    if (guid) {
      actionType = 'update';
    }

    update(itemData, actionType);
  };

  const renderClearButton = (onClick) => {
    return (
      <IconButton
        onClick={onClick}
        style={{
          marginLeft: '0.5rem',
          marginBottom: '0.5rem',
          background: '#f5f5f5'
        }}
        size="small"
        title="Limpar"
      >
        <MaterialIcon icon="close" />
      </IconButton>
    );
  };

  return (
    <div className="rowForm" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div className="containerTop" style={{ maxWidth: '34rem', width: '85vw', flexShrink: 0 }}>
        <div><h1>{res.DADOS} - {titleType}</h1></div>
        <div className="centerTop"></div>
        <div>
          <Button variant="contained" onClick={onClose} className="popUpButton">
            {res.CANCELAR}
          </Button> &nbsp;
          <Button type="submit" variant="contained" color="primary" className="popUpButton" onClick={updateData}>
            <SaveIcon className="iconMapS" />
            {res.SALVAR}
          </Button>
        </div>
      </div>

      <div
        className="notTooBig"
        style={{
          marginBottom: '1rem',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: '0.25rem'
        }}
      >
        {type === 'Polygon' && (
          <div style={{ marginBottom: '1rem', flexShrink: 0 }}>
            <Tabs
              value={areaTab}
              onChange={handleChangeTab}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label={res.SEM_VINCULO || 'Sem vínculo'} />
              <Tab label={res.CLIENTE || 'Cliente'} />
            </Tabs>
          </div>
        )}

        {type === 'Point' && runways && runways.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '0.5rem', flexShrink: 0 }}>
            <TextField
              select
              id="existing_runway"
              label={res.PISTA}
              value={selectedRunwayValue}
              onChange={handleSelectRunway}
              margin="normal"
              fullWidth
            >
              <MenuItem value="">
                {res.SELECIONE}
              </MenuItem>
              {runways.map((runway) => {
                const runwayValue = String(runway.guid || runway.id || '');
                return (
                  <MenuItem key={runwayValue} value={runwayValue}>
                    {runway.name}
                  </MenuItem>
                );
              })}
            </TextField>

            {selectedRunwayValue ? renderClearButton(clearRunwayStep) : null}
          </div>
        )}

        {type === 'Polygon' && areaTab === 0 && registeredAreas && registeredAreas.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '0.5rem', flexShrink: 0 }}>
            <TextField
              select
              id="existing_area"
              label={res.AREA_CADASTRADA}
              value={selectedAreaGuid}
              onChange={handleSelectArea}
              margin="normal"
              fullWidth
            >
              <MenuItem value="">
                {res.SELECIONE}
              </MenuItem>
              {registeredAreas.map((a) => (
                <MenuItem key={a.guid} value={a.guid}>
                  {a.name}
                </MenuItem>
              ))}
            </TextField>

            {selectedAreaGuid ? renderClearButton(clearSemVinculoArea) : null}
          </div>
        )}

        {type === 'Polygon' && areaTab === 1 && (
          <div style={{ marginBottom: '0.5rem', flexShrink: 0 }}>
            {registeredAreaClients && registeredAreaClients.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '0.5rem' }}>
                <TextField
                  select
                  id="existing_area_client"
                  label={res.CLIENTE}
                  value={selectedClientId}
                  onChange={handleSelectClient}
                  margin="normal"
                  fullWidth
                >
                  <MenuItem value="">
                    {res.SELECIONE}
                  </MenuItem>
                  {registeredAreaClients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </TextField>

                {selectedClientId ? renderClearButton(clearClientStep) : null}
              </div>
            )}

            {selectedClientId && filteredProperties && filteredProperties.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '0.5rem' }}>
                <TextField
                  select
                  id="existing_area_property"
                  label={res.PROPRIEDADE}
                  value={selectedPropertyId}
                  onChange={handleSelectProperty}
                  margin="normal"
                  fullWidth
                >
                  <MenuItem value="">
                    {res.SELECIONE}
                  </MenuItem>
                  {filteredProperties.map((property) => (
                    <MenuItem key={property.id} value={property.id}>
                      {property.name}
                    </MenuItem>
                  ))}
                </TextField>

                {selectedPropertyId ? renderClearButton(clearPropertyStep) : null}
              </div>
            )}

            {selectedPropertyId && filteredFolders && filteredFolders.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '0.5rem' }}>
                <TextField
                  select
                  id="existing_area_folder"
                  label={res.PASTA || 'Pasta'}
                  value={selectedFolderId}
                  onChange={handleSelectFolder}
                  margin="normal"
                  fullWidth
                >
                  <MenuItem value="">
                    {res.TODOS}
                  </MenuItem>
                  {filteredFolders.map((folder) => (
                    <MenuItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </MenuItem>
                  ))}
                </TextField>

                {selectedFolderId ? renderClearButton(clearFolderStep) : null}
              </div>
            )}

            {selectedPropertyId && filteredTreeAreas && filteredTreeAreas.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '0.5rem' }}>
                <TextField
                  select
                  id="existing_area_tree"
                  label={res.AREA_CADASTRADA}
                  value={selectedTreeAreaGuid}
                  onChange={handleSelectTreeArea}
                  margin="normal"
                  fullWidth
                >
                  <MenuItem value="">
                    {res.SELECIONE}
                  </MenuItem>
                  {filteredTreeAreas.map((area) => (
                    <MenuItem key={area.guid} value={area.guid}>
                      {area.name}
                    </MenuItem>
                  ))}
                </TextField>

                {selectedTreeAreaGuid ? renderClearButton(clearTreeAreaStep) : null}
              </div>
            )}
          </div>
        )}

        <div style={{ flexShrink: 0 }}>
          <TextField
            id="name"
            label={res.NOME}
            value={name}
            onChange={handleChange(setName)}
            margin="normal"
            fullWidth
            onInput={(e) => {
              e.target.value = e.target.value.toString().slice(0, 45);
            }}
          />
          <TextField
            id="lat"
            label={res.LATITUDE}
            value={lat}
            onChange={handleChange(setLat)}
            margin="normal"
            fullWidth
            onInput={(e) => {
              e.target.value = e.target.value.toString().slice(0, 45);
            }}
          />
          <TextField
            id="lng"
            label={res.LONGITUDE}
            value={lng}
            onChange={handleChange(setLng)}
            margin="normal"
            fullWidth
            onInput={(e) => {
              e.target.value = e.target.value.toString().slice(0, 45);
            }}
          />
        </div>

        <Paper
          elevation={3}
          style={{
            marginTop: '1.5rem',
            flex: 1,
            minHeight: type === 'Point' ? '240px' : '300px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {displayPointsPlace ? (
            <div id="mapid" style={{ height: '100%', width: '100%', flex: 1 }}></div>
          ) : (
            <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ textAlign: 'right', padding: '1rem', flexShrink: 0 }}>
                <label htmlFor="contained-button-file-area" style={{ marginBottom: 0, display: 'inline' }}>
                  <input
                    accept=".jpg, .bmp, .png"
                    style={{ display: 'none' }}
                    id="contained-button-file-area"
                    multiple
                    type="file"
                    onChange={loadImage}
                  />
                  <label htmlFor="contained-button-file-area">
                    <Button component="span" className="buttonS buttonMenu" title={res.IMPORTAR_AREAS}>
                      <MaterialIcon icon="attach_file" className="iconMap" />
                    </Button>
                  </label>
                </label>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 0 }}>
                <img src={img_place} style={{ height: 'auto', width: 'auto', maxWidth: '90%', maxHeight: '90%' }} alt="" />
              </div>
            </div>
          )}
        </Paper>
      </div>
    </div>
  );
};

export default Box;
